/* eslint-disable */
import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Autocomplete,
	TextField,
	Chip,
	CircularProgress,
	IconButton,
	Tooltip,
} from '@mui/material';
import { RefreshCcw, X, FileText, Download } from 'lucide-react';
import { BackdropLoader } from '../../../Components/Loaders';
import VariantTable from '../../Food Products/VariantPrice/VariantTable';
import { notificationServices } from '../../../Services';
import departmentServices from '../../../Services/DepartmentServices/departmentServices';
import userRolesAndPermissions from '../../../Services/userRolesAndPermissions.services';
import { useAuth } from '@/Components/Auth/Login/auth';
import { urls } from '@/Constants';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import wastageAction from '@/Actions/wastage.action';
import drawerActions from '@/Actions/drawer.actions';
import GlobalSearch from '@/Components/globalSearch/search';
import { filtersInfoChip } from '@/Helpers';
import { Filter } from 'lucide-react';
import { useRef } from 'react';
import ImagesFinder from '@/Components/ImagesFinder/ImagesFinder';

// ─── Table Config ────────────────────────────────────────────────────────────

const VARIANT_COLUMNS = [
	{ key: 'id', title: 'ID', width: 80, customCol: true },
	{ key: 'storeName', title: 'Store', width: 100, customCol: true },
	{ key: 'roleNames', title: 'Departments', width: 100, customCol: true },
	{ key: 'createdBy', title: 'Created By', width: 100, customCol: true },
	{ key: 'status', title: 'Status', width: 100, customCol: true },
	{ key: 'createdAt', title: 'Created At', width: 100, customCol: true },
];

const statusList = [
	{ label: 'Pending', type: 'PENDING' },
	{ label: 'Cancelled', type: 'CANCELLED' },
];

// ─── Start Audit Modal ────────────────────────────────────────────────────────

function StartAuditModal({ open, onClose, onConfirm, storesList, departmentsList, departmentsLoading, userId }) {
	const [selectedStores, setSelectedStores] = useState(null);
	const [selectedDepartments, setSelectedDepartments] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const handleClose = () => {
		setSelectedStores(null);
		setSelectedDepartments(null);
		onClose();
	};

	const handleSubmit = async () => {
		if (!selectedStores) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please select at least one store',
			});
			return;
		}
		if (!selectedDepartments) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please select at least one department',
			});
			return;
		}
		setSubmitting(true);
		await onConfirm({
			storeIds: [selectedStores.id],
			roleIds: [selectedDepartments.id],
			userId,
		});
		setSubmitting(false);
		handleClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth='sm'
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: 2,
					boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
				},
			}}
		>
			<DialogTitle
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					pb: 2,
					fontWeight: 600,
					fontSize: 20,
					background: '#fff',
					color: 'rgba(0, 0, 0, 0.87)',
					borderBottom: '1px solid #e2e8f0',
				}}
			>
				Start Department Audit
				<IconButton onClick={handleClose} size='small' sx={{ color: 'rgba(0, 0, 0, 0.54)' }}>
					<X size={20} />
				</IconButton>
			</DialogTitle>

			<DialogContent sx={{ pt: 6, pb: 1, mt: 2 }}>
				{/* Store selector */}
				<Autocomplete
					options={storesList ?? []}
					getOptionLabel={(opt) => opt?.name ?? ''}
					value={selectedStores}
					onChange={(_, val) => setSelectedStores(val)}
					isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
					renderTags={(value, getTagProps) =>
						value.map((option, index) => (
							<Chip
								key={option.id}
								label={option.name}
								size='small'
								color='primary'
								{...getTagProps({ index })}
							/>
						))
					}
					renderInput={(params) => (
						<TextField
							{...params}
							label='Select Stores *'
							placeholder='Search stores…'
							variant='outlined'
							size='small'
						/>
					)}
					sx={{ mb: 3 }}
				/>

				{/* Department selector */}
				<Autocomplete
					options={departmentsList}
					getOptionLabel={(opt) => opt?.userRole ?? ''}
					value={selectedDepartments}
					onChange={(_, val) => setSelectedDepartments(val)}
					isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
					loading={departmentsLoading}
					renderTags={(value, getTagProps) =>
						value.map((option, index) => (
							<Chip
								key={option.id}
								label={option.userRole}
								size='small'
								color='secondary'
								variant='outlined'
								{...getTagProps({ index })}
							/>
						))
					}
					renderInput={(params) => (
						<TextField
							{...params}
							label='Select Departments *'
							placeholder='Search departments…'
							variant='outlined'
							size='small'
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<>
										{departmentsLoading ? <CircularProgress size={16} /> : null}
										{params.InputProps.endAdornment}
									</>
								),
							}}
						/>
					)}
					sx={{ mb: 2 }}
				/>

				{/* User ID (read-only info) */}
				<TextField
					label='User ID'
					value={userId ?? '-'}
					size='small'
					fullWidth
					disabled
					variant='outlined'
					helperText='Logged-in user will be associated with this audit'
				/>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
				<Button
					onClick={handleClose}
					variant='outlined'
					color='inherit'
					disabled={submitting}
					sx={{ borderRadius: 1.5, px: 3 }}
				>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					variant='contained'
					disabled={submitting}
					sx={{
						background: '#facc15',
						'&:hover': { background: '#eab308' },
						color: '#000',
						borderRadius: 1.5,
						px: 3,
						textTransform: 'none',
						fontWeight: 500,
					}}
					startIcon={
						submitting ? <CircularProgress size={16} color='inherit' /> : null
					}
				>
					{submitting ? 'Starting…' : 'Start Audit'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

// ─── Main Page ────────────────────────────────────────────────────────────────

function MyDepartmentsAudit(props) {
	const { storesList, departmentsAuditList, fetchDepartmentsAuditList, toggleFiltersDrawer } = props;
	const { userConfig = {} } = useAuth();
	const { userDetails = {} } = userConfig || {};
	const level = userDetails?.level;
	const userId = userDetails?.id;
	const history = useHistory();
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const departmentsData = useSelector((state) => state.wastage.departmentsList);
	const usersList = useSelector((state) => state.wastage.usersList);
	const [isDeptsLoading, setIsDeptsLoading] = useState(false);
	const [filterValue, setFilterValue] = useState({});
	const [search, setSearch] = useState(null);
	const [isRefresh, setIsRefresh] = useState(false);

	const filtersInfo = {
		header: {
			store: false,
			date: false,
			statusAndTypes: false,
		},
		store: { text: 'Store', show: true },
		dateRangeOnly: { text: 'Date Range', show: true },
		type: { text: 'Status', list: statusList, show: true },
	};

	const dropDownItems = [
		{ label: 'Audit ID', value: 'id', placeholder: 'Search by Audit ID' },
		{ label: 'Store', value: 'storeName', placeholder: 'Search by Store' },
		{ label: 'Department', value: 'roleNames', placeholder: 'Search by Department' },
	];

	const handleFilterDrawerOpen = () => {
		dispatch(toggleFiltersDrawer(true));
	};

	// ── Fetch users ───────────────────────────────────────────────────────────

	const fetchUsers = async () => {
		if (usersList.length > 0) return;
		try {
			const response = await userRolesAndPermissions.getUsersList();
			if (response?.statusCode === 200) {
				const data = response?.data ?? [];
				dispatch(wastageAction.setUsersList(data));
			}
		} catch (error) {
			console.error('Failed to fetch users:', error);
		}
	};

	// ── Fetch departments ─────────────────────────────────────────────────────

	const fetchDepartments = async () => {
		if (departmentsData.length > 0) return;
		setIsDeptsLoading(true);
		try {
			const response = await departmentServices.fetchDepartmentsList();
			if (response?.statusCode === 200) {
				const data = response?.data ?? [];
				dispatch(wastageAction.setDepartmentsList(data));
			}
		} catch (error) {
			console.error('Failed to fetch departments:', error);
		} finally {
			setIsDeptsLoading(false);
		}
	};

	// ── Fetch audit list ──────────────────────────────────────────────────────
	useEffect(() => {
		fetchDepartments();
		fetchUsers();
	}, []);

	// ── Start audit ───────────────────────────────────────────────────────────

	const handleStartAudit = async ({ storeIds, roleIds, userId }) => {
		try {
			const req = { storeIds, roleIds, userId };
			const response = await departmentServices.startDepartmentsAudit(req);
			if (response?.statusCode === 200) {
				notificationServices.generateNotification({
					type: 'success',
					message: response?.message ?? 'Audit started successfully',
				});
				fetchDepartmentsAuditList(filterValue);
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: response?.message ?? 'Failed to start audit',
				});
			}
		} catch (error) {
			console.error(error);
			notificationServices.generateNotification({
				type: 'error',
				message: 'An error occurred while starting the audit',
			});
		}
	};

	// ── Special cells ─────────────────────────────────────────────────────────

	const customRowData = {
		id: (obj) => (
			<div className='flex justify-start w-full text-[12px]'>
				{obj?.id ?? '-'}
			</div>
		),
		storeName: (obj) => {
			const store = storesList?.find((s) => s.id === obj?.storeId);
			return (
				<div className='flex justify-start w-full text-[12px]'>
					{store?.name ?? obj?.storeId ?? '-'}
				</div>
			);
		},
		createdAt: (obj) => (
			<div className='flex justify-start w-full text-[12px]'>
				{obj?.createdAt ?? '-'}
			</div>
		),
		createdBy: (obj) => {
			const user = usersList?.find((u) => u.id === obj?.createdBy);
			return (
				<div className='flex justify-start w-full text-[12px]'>
					{user?.displayName ?? user?.username ?? obj?.createdBy ?? '-'}
				</div>
			);
		},
		status: (obj) => {
			const statusColors = {
				ACTIVE: { bg: '#dcfce7', color: '#166534' },
				COMPLETED: { bg: '#eff6ff', color: '#1e40af' },
				CANCELLED: { bg: '#fee2e2', color: '#991b1b' },
				SCHEDULED: { bg: '#fef9c3', color: '#713f12' },
			};
			const status = obj?.status;
			const style = statusColors[status] ?? { bg: '#f3f4f6', color: '#374151' };
			return (
				<div className='flex justify-start w-full text-[12px]'>
					{!status ? (
						'-'
					) : (
						<span
							style={{
								background: style.bg,
								color: style.color,
								borderRadius: 6,
								padding: '4px 12px',
								fontWeight: 700,
								fontSize: 12,
								textTransform: 'uppercase',
								display: 'inline-flex',
								alignItems: 'center',
								justifyContent: 'center',
								minWidth: '100px',
								boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
							}}
						>
							{status}
						</span>
					)}
				</div>
			);
		},
		roleNames: (obj) => {
			const dept = departmentsData?.find((d) => d.id === obj?.roleId);
			return (
				<div className='flex justify-start w-full text-[12px]'>
					{dept?.userRole ?? obj?.roleId ?? '-'}
				</div>
			);
		},
	};

	const handleFetchAuditList = async (filters) => {
		setFilterValue(filters);
		await fetchDepartmentsAuditList(filters);
		setIsRefresh(false);
	};

	const filteredData = useMemo(() => {
		if (!search?.searchValue) return departmentsAuditList;
		const searchKey = search?.value;
		const term = (search?.searchValue || '').toString().toLowerCase();

		return departmentsAuditList.filter((item) => {
			if (searchKey === 'id') {
				return item?.id?.toString().toLowerCase().includes(term);
			}
			if (searchKey === 'storeName') {
				const store = storesList?.find((s) => s.id === item?.storeId);
				const name = (store?.name || item?.storeId || '').toString().toLowerCase();
				return name.includes(term);
			}
			if (searchKey === 'roleNames') {
				const dept = departmentsData?.find((d) => d.id === item?.roleId);
				const name = (dept?.userRole || item?.roleId || '').toString().toLowerCase();
				return name.includes(term);
			}
			return true;
		});
	}, [search, departmentsAuditList, storesList, departmentsData]);

	// ── Render ────────────────────────────────────────────────────────────────

	return (
		<div style={{ padding: '16px' }}>
			<BackdropLoader showProgress isLoading={isLoading} />
			<StartAuditModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				onConfirm={handleStartAudit}
				storesList={storesList}
				departmentsList={departmentsData}
				departmentsLoading={isDeptsLoading}
				userId={userId}
			/>
			<ImagesFinder
				onSubmit={handleFetchAuditList}
				filtersInfo={filtersInfo}
				exportInfo={{ hideExportAccordian: true }}
			/>

			{/* Header */}
			<div className='flex items-center justify-between mb-4'>
				<span className='font-bold text-[18px]'>
					Department Audits
				</span>
				<div className='flex gap-2'>
					<Tooltip title='Refresh list'>
						<IconButton
							size='small'
							onClick={async () => {
								setIsRefresh(true);
								await handleFetchAuditList(filterValue);
							}}
							disabled={isLoading}
						>
							<RefreshCcw size={18} />
						</IconButton>
					</Tooltip>
					{level !== 1 && (
						<Button
							variant='contained'
							onClick={() => setModalOpen(true)}
							sx={{
								background: '#facc15',
								'&:hover': {
									background: '#eab308',
								},
								color: '#000',
								borderRadius: 2,
								textTransform: 'none',
								fontWeight: 500,
							}}
						>
							+ Start Department Audit
						</Button>
					)}
				</div>
			</div>

			<div className='flex gap-3 w-full justify-start items-center py-1'>
				<div className='w-[85%]'>
					<GlobalSearch
						dropDownOptions={dropDownItems}
						setSelectedValue={setSearch}
						isRefresh={isRefresh}
					/>
				</div>
				<Button
					onClick={handleFilterDrawerOpen}
					className='flex justify-center gap-2 px-4 py-2 text-white bg-teal-600 border border-gray-300 rounded-md hover:bg-teal-700'
				>
					<span className='text-nowrap'>Filter & Export</span>
					<Filter className='size-4' />
				</Button>
			</div>

			{filtersInfoChip(filtersInfo, filterValue)}

			{/* List */}
			<div className='h-[calc(100vh-300px)]'>
				<VariantTable
					columns={VARIANT_COLUMNS}
					data={filteredData?.map((item) => ({
						...item,
						onClick: (obj) => {
							history.push(`${urls.EXTENSION}${urls.DEPARTMENTS_DETAIL}?id=${obj.id}`);
						},
					}))}
					customCol={(column) => (
						<div className='w-full text-left text-[14px] font-semibold text-gray-700 px-4'>
							{column.title}
						</div>
					)}
					customRowData={customRowData}
					isLoading={isLoading}
				/>
			</div>
		</div>
	);
}

// ─── Redux ────────────────────────────────────────────────────────────────────

const mapStateToProps = (state) => ({
	storesList: state.form.STORES?.data?.LIST ?? [],
	departmentsAuditList: state.wastage.departmentsAuditList ?? [],
});

const mapDispatchToProps = {
	fetchDepartmentsAuditList: wastageAction.fetchDepartmentsAuditList,
	toggleFiltersDrawer: drawerActions.toggleFiltersDrawer,
};

export default connect(mapStateToProps, mapDispatchToProps)(MyDepartmentsAudit);
